import { createMask, type Mask } from './mask';

/**
 * Procedural silhouette sources.
 *
 * All shapes are drawn with integer arithmetic at a canonical resolution and then put
 * through the same rescale/cleanup pipeline as authored bitmap masks, so a stencil's
 * behaviour never depends on how it was authored. Procedural shapes also act as the
 * guaranteed-available fallback when an authored mask fails validation.
 */

export type ProceduralShapeId =
  | 'rectangle'
  | 'disc'
  | 'diamond'
  | 'ring'
  | 'cross'
  | 'chevron'
  | 'triangle'
  | 'hourglass'
  | 'spiral'
  | 'arch'
  | 'wave'
  | 'frame'
  | 'pill'
  | 'stairs'
  | 'hexagon'
  | 'octagon'
  | 'squircle'
  | 'shield'
  | 'egg'
  | 'trapezoid';

export const CANONICAL_RESOLUTION = 96;

type ShapeDrawer = (width: number, height: number) => Mask;

function build(width: number, height: number, test: (x: number, y: number) => boolean): Mask {
  const mask = createMask(width, height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (test(x, y)) mask.cells[y * width + x] = 1;
    }
  }
  return mask;
}

/** Doubled coordinates keep the centre exact for even sizes without using halves. */
function centred(value: number, size: number): number {
  return 2 * value - (size - 1);
}

const SHAPES: Record<ProceduralShapeId, ShapeDrawer> = {
  rectangle: (w, h) => build(w, h, () => true),

  disc: (w, h) =>
    build(w, h, (x, y) => {
      const dx = centred(x, w) * h;
      const dy = centred(y, h) * w;
      const radius = w * h;
      return dx * dx + dy * dy <= radius * radius;
    }),

  diamond: (w, h) =>
    build(w, h, (x, y) => {
      const dx = Math.abs(centred(x, w)) * h;
      const dy = Math.abs(centred(y, h)) * w;
      return dx + dy <= w * h;
    }),

  ring: (w, h) =>
    build(w, h, (x, y) => {
      const dx = centred(x, w) * h;
      const dy = centred(y, h) * w;
      const outer = w * h;
      const inner = (outer * 11) / 20;
      const distanceSquared = dx * dx + dy * dy;
      return distanceSquared <= outer * outer && distanceSquared >= inner * inner;
    }),

  cross: (w, h) =>
    build(w, h, (x, y) => {
      const armX = Math.abs(centred(x, w));
      const armY = Math.abs(centred(y, h));
      return armX <= w * 0.34 || armY <= h * 0.34;
    }),

  chevron: (w, h) =>
    build(w, h, (x, y) => {
      const dx = Math.abs(centred(x, w)) / 2;
      const band = (dx * h) / w;
      const top = band;
      return y >= top && y <= top + h * 0.45;
    }),

  triangle: (w, h) =>
    build(w, h, (x, y) => {
      const halfSpan = ((y + 1) * w) / (2 * h);
      return Math.abs(centred(x, w)) / 2 <= halfSpan;
    }),

  hourglass: (w, h) =>
    build(w, h, (x, y) => {
      const relative = Math.abs(centred(y, h)) / 2;
      const halfSpan = (relative * w) / h / 1.15 + w * 0.12;
      return Math.abs(centred(x, w)) / 2 <= halfSpan;
    }),

  spiral: (w, h) => {
    // Rectangular spiral drawn by walking inwards; thickness scales with the grid.
    const mask = createMask(w, h);
    const thickness = Math.max(1, Math.floor(Math.min(w, h) / 12));
    const gap = thickness * 2;
    let left = 0;
    let right = w - 1;
    let top = 0;
    let bottom = h - 1;
    let guard = 0;
    while (left <= right && top <= bottom && guard < 64) {
      guard += 1;
      for (let x = left; x <= right; x += 1) {
        for (let t = 0; t < thickness && top + t <= bottom; t += 1)
          mask.cells[(top + t) * w + x] = 1;
      }
      for (let y = top; y <= bottom; y += 1) {
        for (let t = 0; t < thickness && right - t >= left; t += 1)
          mask.cells[y * w + (right - t)] = 1;
      }
      for (let x = left; x <= right; x += 1) {
        for (let t = 0; t < thickness && bottom - t >= top; t += 1)
          mask.cells[(bottom - t) * w + x] = 1;
      }
      for (let y = top + gap + thickness; y <= bottom; y += 1) {
        for (let t = 0; t < thickness && left + t <= right; t += 1)
          mask.cells[y * w + (left + t)] = 1;
      }
      left += gap + thickness;
      right -= gap + thickness;
      top += gap + thickness;
      bottom -= gap + thickness;
    }
    return mask;
  },

  arch: (w, h) =>
    build(w, h, (x, y) => {
      const legWidth = w * 0.26;
      const insideX = Math.abs(centred(x, w)) / 2 <= w / 2 - legWidth;
      const archTop = h * 0.42;
      if (y >= archTop) return !insideX;
      const dx = centred(x, w) / 2;
      const dy = (archTop - y) * (w / 2 / archTop);
      const outer = w / 2;
      const inner = w / 2 - legWidth;
      const distance = dx * dx + dy * dy;
      return distance <= outer * outer && distance >= inner * inner;
    }),

  wave: (w, h) =>
    build(w, h, (x, y) => {
      // Triangle wave instead of a sine: exact integer behaviour in every runtime.
      const period = Math.max(4, Math.floor(w / 2));
      const phase = x % period;
      const half = period / 2;
      const rise = phase < half ? phase / half : (period - phase) / half;
      const centre = h * 0.25 + rise * h * 0.5;
      return Math.abs(y - centre) <= h * 0.16;
    }),

  frame: (w, h) =>
    build(w, h, (x, y) => {
      const border = Math.max(1, Math.floor(Math.min(w, h) * 0.18));
      return x < border || y < border || x >= w - border || y >= h - border;
    }),

  pill: (w, h) =>
    build(w, h, (x, y) => {
      const radius = Math.min(w, h) / 2;
      const cx = Math.min(Math.max(x + 0.5, radius), w - radius);
      const cy = Math.min(Math.max(y + 0.5, radius), h - radius);
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      return dx * dx + dy * dy <= radius * radius;
    }),

  // The solid family. These carry most of the campaign because a filled silhouette packs
  // arrows into shared rows and columns, which is where the puzzle lives; see solidity.ts.
  hexagon: (w, h) =>
    build(w, h, (x, y) => {
      const nx = Math.abs(centred(x, w)) / (w - 1);
      const ny = Math.abs(centred(y, h)) / (h - 1);
      // Flat top and bottom across the middle half, tapering to points at the sides.
      return ny <= 1 && nx <= 1 && ny <= 1 - Math.max(0, nx * 2 - 1);
    }),

  octagon: (w, h) =>
    build(w, h, (x, y) => {
      const nx = Math.abs(centred(x, w)) / (w - 1);
      const ny = Math.abs(centred(y, h)) / (h - 1);
      return nx <= 1 && ny <= 1 && nx + ny <= 1.4;
    }),

  squircle: (w, h) =>
    build(w, h, (x, y) => {
      const nx = Math.abs(centred(x, w)) / (w - 1);
      const ny = Math.abs(centred(y, h)) / (h - 1);
      // Superellipse of order four, written as repeated multiplication.
      const sx = nx * nx;
      const sy = ny * ny;
      return sx * sx + sy * sy <= 1;
    }),

  shield: (w, h) =>
    build(w, h, (x, y) => {
      const nx = Math.abs(centred(x, w)) / (w - 1);
      const shoulder = 0.55;
      const ny = y / (h - 1);
      if (ny <= shoulder) return nx <= 1;
      const drop = (ny - shoulder) / (1 - shoulder);
      return nx <= 1 - drop * drop;
    }),

  egg: (w, h) =>
    build(w, h, (x, y) => {
      const dx = centred(x, w) / (w - 1);
      const ny = y / (h - 1);
      // An ellipse whose half-width swells towards the base.
      const halfWidth =
        Math.sqrt(Math.max(0, 1 - (2 * ny - 1) * (2 * ny - 1))) * (0.78 + ny * 0.22);
      return Math.abs(dx) <= halfWidth;
    }),

  trapezoid: (w, h) =>
    build(w, h, (x, y) => {
      const nx = Math.abs(centred(x, w)) / (w - 1);
      const ny = y / (h - 1);
      return nx <= 0.45 + ny * 0.55;
    }),

  stairs: (w, h) => {
    const steps = 5;
    const stepWidth = w / steps;
    const stepHeight = h / steps;
    return build(w, h, (x, y) => {
      const step = Math.floor(x / stepWidth);
      return y >= h - (step + 1) * stepHeight;
    });
  },
};

export function drawProceduralShape(
  shape: ProceduralShapeId,
  width = CANONICAL_RESOLUTION,
  height = CANONICAL_RESOLUTION,
): Mask {
  const drawer = SHAPES[shape];
  if (drawer === undefined) throw new Error(`Unknown procedural shape: ${shape}`);
  return drawer(width, height);
}

export const PROCEDURAL_SHAPE_IDS = Object.keys(SHAPES) as ProceduralShapeId[];
